import React, { Component } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import CollaborationIcon from "./CollaborationIcon";
import {
  formatArtifactDate,
  getOwnerProfile,
  sortArtifacts
} from "../collaborationUtils";

class ArtifactList extends Component {
  static propTypes = {
    artifacts: PropTypes.arrayOf(PropTypes.object).isRequired,
    isInternalUser: PropTypes.bool.isRequired,
    onArtifactClick: PropTypes.func
  };

  state = {
    sortBy: "date",
    sortDirection: "desc"
  };

  handleSort = column => {
    this.setState(prevState => {
      if (prevState.sortBy === column) {
        return {
          sortDirection: prevState.sortDirection === "asc" ? "desc" : "asc"
        };
      }

      return { sortBy: column, sortDirection: "asc" };
    });
  };

  renderSortArrow = column => {
    const { sortBy, sortDirection } = this.state;
    if (sortBy !== column) {
      return <SortArrow><CollaborationIcon name="chevron-down" size={12} /></SortArrow>;
    }

    return (
      <SortArrow $active>
        <CollaborationIcon
          name={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
          size={12}
        />
      </SortArrow>
    );
  };

  render() {
    const { artifacts, isInternalUser, onArtifactClick } = this.props;
    const { sortBy, sortDirection } = this.state;

    if (!artifacts.length) {
      return (
        <EmptyState>
          Bisher noch keine weiteren Sites.
          <br />
          Bei zukünftigen Terminen, Workshops oder Reviews findest du sie hier.
        </EmptyState>
      );
    }

    const sortedArtifacts = sortArtifacts(artifacts, sortBy, sortDirection);

    return (
      <DocList $isCustomerView={!isInternalUser}>
        <DocRowHeader $isCustomerView={!isInternalUser}>
          <HeaderCell
            type="button"
            $sorted={sortBy === "date"}
            onClick={() => this.handleSort("date")}
          >
            Datum {this.renderSortArrow("date")}
          </HeaderCell>
          <HeaderCell
            type="button"
            $sorted={sortBy === "title"}
            onClick={() => this.handleSort("title")}
          >
            Titel {this.renderSortArrow("title")}
          </HeaderCell>
          <HeaderCell
            type="button"
            $sorted={sortBy === "owner"}
            onClick={() => this.handleSort("owner")}
          >
            Eigentümer {this.renderSortArrow("owner")}
          </HeaderCell>
          {isInternalUser && (
            <HeaderCell
              type="button"
              $sorted={sortBy === "shared"}
              onClick={() => this.handleSort("shared")}
            >
              Geteilt {this.renderSortArrow("shared")}
            </HeaderCell>
          )}
          <HeaderCellSpacer />
        </DocRowHeader>
        {sortedArtifacts.map(artifact => {
          const owner = getOwnerProfile(artifact.owner);
          const isShared = artifact.visibility === "shared";

          return (
            <DocRow
              key={artifact.key}
              type="button"
              $isCustomerView={!isInternalUser}
              onClick={() => onArtifactClick?.(artifact)}
            >
              <DocDate>{formatArtifactDate(artifact.date)}</DocDate>
              <DocTitle>{artifact.title}</DocTitle>
              <DocOwner>
                <OwnerAvatar>{owner.initials}</OwnerAvatar>
                <span>{owner.name}</span>
              </DocOwner>
              {isInternalUser && (
                <DocShared $isShared={isShared}>
                  <CollaborationIcon
                    name={isShared ? "users" : "lock"}
                    size={14}
                  />
                  {isShared ? "geteilt" : "intern"}
                </DocShared>
              )}
              <DocAction>
                <CollaborationIcon name="arrow-right" size={14} />
              </DocAction>
            </DocRow>
          );
        })}
      </DocList>
    );
  }
}

export default ArtifactList;

const DocList = styled.div`
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const docRowGrid = ({ $isCustomerView }) =>
  $isCustomerView
    ? "130px 1fr 170px 40px"
    : "130px 1fr 170px 110px 40px";

const DocRowHeader = styled.div`
  display: grid;
  grid-template-columns: ${docRowGrid};
  gap: 16px;
  align-items: center;
  padding: 10px 16px;
  background: #faf9f5;
  font-size: 10px;
  font-weight: 700;
  color: #a09e96;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const HeaderCell = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  color: ${({ $sorted }) => ($sorted ? "#0a7c4e" : "inherit")};
  text-transform: inherit;
  letter-spacing: inherit;
  cursor: pointer;
  user-select: none;
  text-align: left;

  &:hover {
    color: #181714;
  }
`;

const HeaderCellSpacer = styled.div``;

const SortArrow = styled.span`
  display: flex;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  color: ${({ $active }) => ($active ? "#0a7c4e" : "inherit")};

  ${HeaderCell}:hover & {
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
  }
`;

const DocRow = styled.button`
  display: grid;
  grid-template-columns: ${docRowGrid};
  gap: 16px;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #f0eee8;
  background: #fff;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background 100ms ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #faf9f5;
  }
`;

const DocDate = styled.div`
  color: #6a6960;
  font-size: 12px;
`;

const DocTitle = styled.div`
  color: #181714;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.35;
`;

const DocOwner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6a6960;
`;

const OwnerAvatar = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e2dfd8;
  color: #5f5e5a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 600;
  flex-shrink: 0;
`;

const DocShared = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ $isShared }) => ($isShared ? "#0a7c4e" : "#a09e96")};
`;

const DocAction = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: transparent;
  color: #c8c5bc;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  transition: background 120ms ease, color 120ms ease;

  ${DocRow}:hover & {
    background: #181714;
    color: #fff;
  }
`;

const EmptyState = styled.div`
  padding: 32px 24px;
  background: #fff;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  text-align: center;
  color: #a09e96;
  font-size: 13px;
  line-height: 1.55;
`;
